import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunity } from '@/contexts/CommunityContext';
import { toast } from '@/components/ui/use-toast';

export interface JobProposalWithDetails {
  id: string;
  service_request_id: string;
  business_id: string;
  quote: number | null;
  response_message: string | null;
  status: 'AWAITING_RESPONSE' | 'PROPOSED' | 'DECLINED' | 'REQUESTED_INFO';
  comment: string | null;
  created_at: string;
  responded_at: string | null;
  service_request: {
    id: string;
    original_request: string;
    town: string | null;
    urgency: string;
    created_at: string;
    attachments?: Array<{
      id: string;
      image_url: string;
      original_filename: string;
    }> | null;
  };
  detailed_quote?: {
    id: string;
    price_type: 'fixed' | 'estimate' | 'range';
    status: 'partial' | 'complete';
    fixed_price: number | null;
    estimated_price: number | null;
    min_price: number | null;
    max_price: number | null;
    notes: string | null;
    created_at: string;
    updated_at: string;
    quote_items?: Array<{
      id: string;
      label: string;
      quantity: number;
      unit_label: string;
      unit_price: number;
      line_total: number;
      display_order: number;
    }> | null;
  } | null;
}

export interface UpdateJobProposalData {
  id: string;
  status?: 'AWAITING_RESPONSE' | 'PROPOSED' | 'DECLINED' | 'REQUESTED_INFO';
  response_message?: string | null;
  quote?: number | null;
  comment?: string | null;
}

export interface CreateQuoteData {
  job_proposal_id: string;
  price_type: 'fixed' | 'estimate' | 'range';
  status: 'partial' | 'complete';
  fixed_price?: number | null;
  estimated_price?: number | null;
  min_price?: number | null;
  max_price?: number | null;
  notes?: string | null;
}

export interface QuoteItemData {
  id?: string;
  quote_id: string;
  label: string;
  quantity: number;
  unit_label: string;
  unit_price: number;
  line_total: number;
  display_order: number;
}

// Hook to fetch job proposals for a business
export const useJobProposals = (options: {
  searchTerm?: string;
  status?: string;
  currentPage?: number;
  pageSize?: number;
} = {}) => {
  const { user } = useAuth();
  const { currentCommunity } = useCommunity();
  
  return useQuery({
    queryKey: ['job-proposals', user?.id, currentCommunity?.id, options],
    queryFn: async () => {
      if (!user || !currentCommunity) return null;

      // First get the business for this user
      const { data: businesses, error: businessError } = await supabase
        .from('businesses')
        .select('id')
        .eq('user_id', user.id)
        .eq('community_id', currentCommunity.id);

      if (businessError) throw businessError;
      if (!businesses || businesses.length === 0) {
        return { proposals: [], totalCount: 0, totalPages: 0 };
      }

      const businessIds = businesses.map(b => b.id);

      let query = (supabase as any)
        .from('job_proposals')
        .select(`
          id,
          service_request_id,
          business_id,
          quote,
          response_message,
          status,
          comment,
          created_at,
          responded_at,
          service_request:service_requests (
            id,
            original_request,
            town,
            urgency,
            created_at,
            attachments:service_request_attachments (
              id,
              image_url,
              original_filename
            )
          ),
          detailed_quote:quotes (
            id,
            price_type,
            status,
            fixed_price,
            estimated_price,
            min_price,
            max_price,
            notes,
            created_at,
            updated_at,
            quote_items (
              id,
              label,
              quantity,
              unit_label,
              unit_price,
              line_total,
              display_order
            )
          )
        `, { count: 'exact' })
        .in('business_id', businessIds);

      // Apply status filter
      if (options.status && options.status !== 'all') {
        query = query.eq('status', options.status);
      }

      // Apply search filter
      if (options.searchTerm) {
        query = query.or(`service_request.original_request.ilike.%${options.searchTerm}%,response_message.ilike.%${options.searchTerm}%`);
      }

      // Apply pagination
      if (options.currentPage && options.pageSize) {
        const from = (options.currentPage - 1) * options.pageSize;
        const to = from + options.pageSize - 1;
        query = query.range(from, to);
      }

      query = query.order('created_at', { ascending: false });

      const { data, error, count } = await query;
      if (error) throw error;

      return {
        proposals: data || [],
        totalCount: count || 0,
        totalPages: Math.ceil((count || 0) / (options.pageSize || 10))
      };
    },
    enabled: !!user && !!currentCommunity
  });
};

// Hook to get proposal status counts
export const useJobProposalCounts = () => {
  const { user } = useAuth();
  const { currentCommunity } = useCommunity();

  return useQuery({
    queryKey: ['job-proposal-counts', user?.id, currentCommunity?.id],
    queryFn: async () => {
      if (!user || !currentCommunity) return null;

      // Get business for this user
      const { data: businesses } = await supabase
        .from('businesses')
        .select('id')
        .eq('user_id', user.id)
        .eq('community_id', currentCommunity.id);

      if (!businesses || businesses.length === 0) {
        return { all: 0, AWAITING_RESPONSE: 0, PROPOSED: 0, DECLINED: 0, REQUESTED_INFO: 0 };
      }

      const businessIds = businesses.map(b => b.id);

      const [allCount, awaitingCount, proposedCount, declinedCount, requestedInfoCount] = await Promise.all([
        (supabase as any).from('job_proposals').select('*', { count: 'exact', head: true }).in('business_id', businessIds),
        (supabase as any).from('job_proposals').select('*', { count: 'exact', head: true }).in('business_id', businessIds).eq('status', 'AWAITING_RESPONSE'),
        (supabase as any).from('job_proposals').select('*', { count: 'exact', head: true }).in('business_id', businessIds).eq('status', 'PROPOSED'),
        (supabase as any).from('job_proposals').select('*', { count: 'exact', head: true }).in('business_id', businessIds).eq('status', 'DECLINED'),
        (supabase as any).from('job_proposals').select('*', { count: 'exact', head: true }).in('business_id', businessIds).eq('status', 'REQUESTED_INFO')
      ]);

      return {
        all: allCount.count || 0,
        AWAITING_RESPONSE: awaitingCount.count || 0,
        PROPOSED: proposedCount.count || 0,
        DECLINED: declinedCount.count || 0,
        REQUESTED_INFO: requestedInfoCount.count || 0
      };
    },
    enabled: !!user && !!currentCommunity
  });
};

// Hook to update job proposal
export const useUpdateJobProposal = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (data: UpdateJobProposalData) => {
      const updateData: any = {
        ...data,
        responded_at: new Date().toISOString()
      };
      
      // Remove id from update data
      const { id, ...updateFields } = updateData;

      const { error } = await (supabase as any)
        .from('job_proposals')
        .update(updateFields)
        .eq('id', id);

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-proposals', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['job-proposal-counts', user?.id] });
      toast({
        title: "Proposal Updated",
        description: "Your job proposal response has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update job proposal",
        variant: "destructive",
      });
    }
  });
};

// Hook to create or update quote
export const useCreateOrUpdateQuote = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (data: CreateQuoteData & { id?: string }) => {
      if (data.id) {
        // Update existing quote
        const { id, ...updateData } = data;
        const { error } = await (supabase as any)
          .from('quotes')
          .update({ ...updateData, updated_at: new Date().toISOString() })
          .eq('id', id);
        if (error) throw error;
        return { ...data, id };
      } else {
        // Create new quote
        const { data: quote, error } = await (supabase as any)
          .from('quotes')
          .insert(data)
          .select()
          .single();
        if (error) throw error;
        return quote;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-proposals', user?.id] });
      toast({
        title: "Quote Saved",
        description: "Your quote has been saved successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save quote",
        variant: "destructive",
      });
    }
  });
};

// Hook to manage quote items
export const useManageQuoteItems = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ 
      quoteId, 
      items 
    }: { 
      quoteId: string; 
      items: QuoteItemData[] 
    }) => {
      // Delete existing items for this quote
      await (supabase as any)
        .from('quote_items')
        .delete()
        .eq('quote_id', quoteId);

      // Insert new items
      if (items.length > 0) {
        const { error } = await (supabase as any)
          .from('quote_items')
          .insert(items.map(item => ({
            quote_id: quoteId,
            label: item.label,
            quantity: item.quantity,
            unit_label: item.unit_label,
            unit_price: item.unit_price,
            line_total: item.line_total,
            display_order: item.display_order
          })));
        
        if (error) throw error;
      }

      return { quoteId, items };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-proposals', user?.id] });
      toast({
        title: "Quote Items Updated",
        description: "Your quote breakdown has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update quote items",
        variant: "destructive",
      });
    }
  });
};